import {useApolloClient} from '@apollo/client';
import {
  useAllRawTransactionsLazyQuery,
  useInboxBacklogSummaryQuery,
  useTransactionsQuery,
} from '@transactions/graphql';
import {backlogCutoff} from 'common/backlogCutoff';
import * as React from 'react';
import {useHotkeys} from 'react-hotkeys-hook';
import {useSearchParams} from 'react-router-dom';
import updateRawTransactions from 'src/apolloCache/updateRawTransactions';
import {DateFilterSelect} from 'src/components/DateFilterSelect';
import {usePrefetchSuggestions} from 'src/InboxQuickReview';
import Empty from 'src/ListView/Empty';
import {MoreButton} from 'src/ListView/MoreButton';
import Loading from 'src/Loading';
import RawTransactionList from 'src/RawTransactionList';
import {Header as RawTransactionHeader} from 'src/RawTransactionListView/Header';
import {RawSelectionBar} from 'src/RawTransactionListView/RawSelectionBar';
import {useRawRowGestures} from 'src/RawTransactionListView/useRawRowGestures';
import {useRawSelectionActions} from 'src/RawTransactionListView/useRawSelectionActions';
import TransactionRow from 'src/TransactionListView/TransactionRow';
import {useSelectionState} from 'src/useSelectionState';
import type {
  AllRawTransactionsQuery,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {DateFilterState} from 'src/components/DateFilterSelect';

const RAW_TX_LIMIT = 10;

const SectionHeading = ({children}: {children: React.ReactNode}) => (
  <div className="bg-muted border-border text-muted-foreground border-b px-4 py-2 text-sm font-medium">
    {children}
  </div>
);

/**
 * The review queue: unallocated raw transactions (newest first) with the
 * full selection / bulk-convert workflow. The initial pages are scoped to
 * the recent era (the same fixed cutoff the inbox summary uses), so the
 * list never reaches into the historical backlog on its own; once the
 * recent era is exhausted, "Show older" continues the same cursor walk past
 * the cutoff. A date filter overrides the scoping entirely (the Backlog's
 * year rows deep-link here with ?start/?end to chip away at one year with
 * the same workflow).
 *
 * Two variants: `full`, the Ledger's Needs review view; and `preview`, the
 * Inbox's review card, where the common case happens without leaving the
 * Inbox (see the preview branch below).
 */
export const ReviewList = ({
  variant,
  expanded = false,
}: {
  variant: 'full' | 'preview';
  /** Preview only: the card has taken over the Inbox (every row, paging). */
  expanded?: boolean;
}) => {
  const client = useApolloClient();
  const [searchParams] = useSearchParams();

  const [dateFilter, setDateFilter] = React.useState<DateFilterState>(() => ({
    startDate: searchParams.get('start'),
    endDate: searchParams.get('end'),
  }));

  React.useEffect(() => {
    const startDate = searchParams.get('start');
    const endDate = searchParams.get('end');
    // The URL owns the filter: a link or Back that changes or clears the
    // params changes it too.
    setDateFilter({startDate, endDate});
  }, [searchParams]);

  // The default (unfiltered) scope starts at the shared backlog cutoff so
  // the initial pages stay within the recent era. Fixed per mount so a
  // session open across midnight can't shift the scope under the query.
  const [recencyFloor] = React.useState(() => backlogCutoff().toISODate());
  const manualFilter =
    dateFilter.startDate !== null || dateFilter.endDate !== null;
  const windowStart = manualFilter ? null : recencyFloor;

  const filters = {
    includeProcessed: false,
    limit: RAW_TX_LIMIT,
    startDate: dateFilter.startDate ?? windowStart,
    endDate: dateFilter.endDate,
  };

  // Whether anything unallocated exists past the cutoff — drives the "Show
  // older" affordance once the recent era is spent. Kept fresh by the
  // refetch in handleUpdateRawTransactions below.
  const {data: summaryData} = useInboxBacklogSummaryQuery();
  const backlogCount = summaryData?.inboxBacklogSummary.backlogCount ?? 0;

  // The other kind of loose end: transactions recorded before their line
  // arrived (pending). They sit above the queue in the full review view, so
  // a line and the transaction waiting for it are on one screen. Not in the
  // Inbox preview (its own card lists them), nor under a manual date filter
  // (a Backlog year is about old lines, not what's pending now).
  const showPending = variant === 'full' && !manualFilter;
  const pendingVariables = {pending: true, startDate: null, endDate: null, limit: 100};
  const {
    data: pendingData,
    error: pendingError,
    loading: pendingLoading,
    fetchMore: fetchMorePending,
    refetch: refetchPending,
  } = useTransactionsQuery({
    skip: !showPending,
    variables: pendingVariables,
    // A transaction marked posted (or newly entered pending) isn't added to
    // or removed from this cached filtered list by its own write — revalidate
    // on every visit, as the Inbox's pending card does.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const pendingPage = showPending ? pendingData?.transactions : undefined;
  const pendingTransactions = pendingPage?.items ?? [];
  const pendingFailed = showPending && pendingError != null && pendingTransactions.length === 0;
  // Still waiting on the first pending answer: an empty queue can't yet be
  // called caught up — something may be pending.
  const pendingUnknown = showPending && pendingData == null && pendingError == null;
  const handleMorePending = () => {
    if (!pendingPage?.hasMore || !pendingPage.endCursor) return;
    // Pages merge in the cache (the transactions field policy).
    void fetchMorePending({
      variables: {...pendingVariables, beforeCursor: pendingPage.endCursor},
    });
  };

  const [
    loadRawTransactions,
    {loading: rawLoading, data: rawData, error: rawError, fetchMore},
  ] = useAllRawTransactionsLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const updateRawTransactionsCache = updateRawTransactions({
    client,
    filters,
  });

  const handleUpdateRawTransactions: typeof updateRawTransactionsCache = (
    mutation,
  ) => {
    updateRawTransactionsCache(mutation);
    // The inbox card's counts come from the summary query; keep them fresh
    void client.refetchQueries({include: ['InboxBacklogSummary']});
  };

  // Rows from a previous date scope must not flash (or be selectable) while
  // the new scope loads — track which scope the displayed data belongs to
  const scopeKey = `${filters.startDate ?? ''}|${filters.endDate ?? ''}`;
  const [loadedScope, setLoadedScope] = React.useState<string | null>(null);

  // Guard completions against out-of-order resolution: only the latest
  // requested scope may mark itself loaded
  const latestScopeRef = React.useRef(scopeKey);
  React.useEffect(() => {
    latestScopeRef.current = scopeKey;
    const requested = scopeKey;
    void loadRawTransactions({variables: filters}).then(() => {
      if (latestScopeRef.current === requested) {
        setLoadedScope(requested);
      }
    });
  }, [dateFilter]);

  const scopeReady = loadedScope === scopeKey;

  const rawTransactions = scopeReady
    ? (rawData?.allRawTransactions?.items ?? [])
    : [];

  const selectionState = useSelectionState<BasicRawTransactionFragment>(
    rawTransactions,
    [],
  );
  const {selected} = selectionState;

  // A scope change replaces the visible rows; stale selections would let the
  // header act on rows the user can no longer see
  // clearSelection is recreated each render; a ref keeps this effect keyed
  // to the scope alone (depending on the function would loop)
  const clearSelectionRef = React.useRef(selectionState.clearSelection);
  clearSelectionRef.current = selectionState.clearSelection;
  React.useEffect(() => {
    clearSelectionRef.current();
  }, [scopeKey]);

  const isPreview = variant === 'preview';

  // Collapsed, the card shows only its first few rows; a selection must never
  // act on rows out of view, so rows that leave the view (the card folding
  // back, or a refresh reordering the list) drop out of it.
  React.useEffect(() => {
    if (!isPreview || expanded) return;
    const visible = new Set(
      rawTransactions.slice(0, COLLAPSED_ROWS).map((rt) => rt._id),
    );
    const kept = selected.filter((rt) => visible.has(rt._id));
    if (kept.length !== selected.length) selectionState.setSelection(kept);
  }, [expanded, rawTransactions, selected]);

  const selectionActions = useRawSelectionActions({
    selectionState,
    onUpdate: handleUpdateRawTransactions,
  });
  // Every row here is unallocated, so a tap opens the create sheet for it.
  const gestures = useRawRowGestures({
    selectionState,
    openCreate: selectionActions.openCreateForRow,
    allocateOnTap: true,
  });
  usePrefetchSuggestions(isPreview ? rawTransactions : []);

  // The full list's header owns Escape; the preview card has no header.
  useHotkeys(
    'Escape',
    () => {
      if (isPreview && gestures.selecting && !selectionActions.anyModalOpen) {
        gestures.stopSelecting();
      }
    },
    [isPreview, gestures.selecting, selectionActions.anyModalOpen],
  );

  const pageHasMore = rawData?.allRawTransactions?.hasMore ?? false;
  // Whether the loaded rows already crossed the recency floor into the
  // backlog. Derived from the data rather than state so it survives
  // collapse/expand cycles and cache-restored lists.
  const extendedPastFloor =
    windowStart !== null &&
    rawTransactions.some((rawTransaction) => rawTransaction.date < windowStart);
  // The recent era is spent but the backlog isn't — "More" becomes "Show
  // older" and drops the date floor.
  const canExtend =
    windowStart !== null &&
    !extendedPastFloor &&
    !pageHasMore &&
    backlogCount > 0;
  const hasMore = pageHasMore || canExtend;
  const moreLabel = canExtend ? 'Show older' : undefined;

  const handleMore = () => {
    if (fetchMore && hasMore) {
      const endCursor = rawData?.allRawTransactions?.endCursor ?? null;
      if (pageHasMore && !endCursor) {
        return;
      }
      // Paging past the recency floor (crossing it now, or having crossed it
      // already) drops the floor so the same cursor walk continues into the
      // backlog. An empty recent era has no cursor; the unbounded first page
      // is entirely backlog anyway.
      const startDate =
        windowStart !== null && (extendedPastFloor || !pageHasMore)
          ? null
          : filters.startDate;
      void fetchMore({
        variables: {
          ...filters,
          startDate,
          ...(endCursor ? {beforeCursor: endCursor} : {}),
        },
        updateQuery: (previous: AllRawTransactionsQuery, result) => {
          const moreItems =
            result.fetchMoreResult?.allRawTransactions?.items ?? [];
          return {
            ...previous,
            allRawTransactions: {
              ...previous.allRawTransactions,
              items: [
                ...(previous.allRawTransactions?.items ?? []),
                ...moreItems,
              ],
              endCursor:
                result.fetchMoreResult?.allRawTransactions?.endCursor ?? null,
              hasMore:
                result.fetchMoreResult?.allRawTransactions?.hasMore ?? false,
            },
          };
        },
      });
    }
  };

  if (!scopeReady && rawLoading) {
    return (
      <div className="flex h-full items-center justify-center py-8">
        <Loading size="medium" />
      </div>
    );
  }

  const header = (
    <RawTransactionHeader
      selectionState={selectionState}
      actions={selectionActions}
      gestures={gestures}
      showSyncButton={false}
      showAddActions={false}
      // One Dates pill and the Select button fit a phone row together, so
      // the actions stay beside the filter instead of dropping to a second
      // line and costing the (internally scrolled) list that height.
      stackActionsOnMobile={false}
      filterContent={
        <DateFilterSelect
          value={dateFilter}
          onChange={setDateFilter}
          // Sharing the row means the pill has to fit beside the actions:
          // "Dates: Custom range…" is wider than a 320px phone leaves it.
          truncateLabel
        />
      }
    />
  );

  const errorBody = (
    <div className="flex flex-col items-center gap-2 py-8">
      <span className="text-muted-foreground text-sm">
        Couldn&apos;t load transactions to review.
      </span>
      <button
        type="button"
        className="text-foreground cursor-pointer text-sm underline"
        onClick={() => {
          const requested = scopeKey;
          void loadRawTransactions({variables: filters}).then(() => {
            if (latestScopeRef.current === requested) {
              setLoadedScope(requested);
            }
          });
        }}
      >
        Retry
      </button>
    </div>
  );

  // An empty list can still have more to load: the recent era may be empty
  // while the backlog isn't (canExtend), or every loaded row was just
  // reviewed away with more pages remaining in scope (pageHasMore).
  const emptyMessage =
    hasMore ? (
      canExtend ? (
        <Empty>Nothing recent to review.</Empty>
      ) : null
    ) : (
      <Empty>All caught up!</Empty>
    );

  const emptyBody = hasMore ? (
    <div>
      {emptyMessage}
      <MoreButton
        loading={rawLoading}
        hasMore
        onMore={handleMore}
        label={moreLabel}
      />
    </div>
  ) : (
    emptyMessage
  );

  // Full: the list fills the view, rows scroll internally, and the
  // pagination footer stays pinned at the bottom of the screen instead of
  // trailing the last row — repeated "More" clicks never chase the button
  // down the page.
  if (variant === 'full') {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {(pendingTransactions.length > 0 || pendingFailed) && (
            <>
              <SectionHeading>Pending</SectionHeading>
              {pendingFailed ? (
                // Never let a failed load read as nothing pending.
                <div className="border-border flex items-center gap-3 border-b px-4 py-3 text-sm">
                  <span className="text-muted-foreground">Couldn&apos;t load pending transactions.</span>
                  <button
                    type="button"
                    className="text-foreground cursor-pointer underline"
                    onClick={() => void refetchPending()}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  {pendingTransactions.map((transaction) => (
                    <TransactionRow
                      key={transaction._id}
                      transaction={transaction}
                      selected={false}
                      isSelectMode={false}
                      onSelect={() => undefined}
                    />
                  ))}
                  {pendingPage?.hasMore && (
                    <MoreButton
                      loading={pendingLoading}
                      hasMore
                      onMore={handleMorePending}
                    />
                  )}
                </>
              )}
              <SectionHeading>To review</SectionHeading>
            </>
          )}
          {rawTransactions.length === 0 && rawError ? (
            errorBody
          ) : rawTransactions.length === 0 ? (
            pendingUnknown ? (
              <div className="flex justify-center py-8">
                <Loading size="medium" />
              </div>
            ) : (
              emptyMessage
            )
          ) : (
            <>
              <RawTransactionList
                transactions={rawTransactions}
                {...gestures.rowProps}
              />
              {/* Exhausted lists mark their end in-flow, where the last row
                  is; only actionable pagination earns the pinned footer */}
              {!hasMore && (
                <MoreButton loading={false} hasMore={false} onMore={handleMore} />
              )}
            </>
          )}
        </div>
        {hasMore && (
          <div className="border-border flex justify-center border-t">
            <MoreButton
              loading={rawLoading}
              hasMore
              onMore={handleMore}
              label={moreLabel}
            />
          </div>
        )}
      </div>
    );
  }

  // Preview (the Inbox card): the first few rows, and every row once the card
  // has taken over the Inbox.
  const listProps = gestures.rowProps;
  return (
    <div>
      {rawTransactions.length === 0 ? (
        // A failed load is never "caught up".
        rawError ? errorBody : emptyBody
      ) : (
        <>
          <RawTransactionList
            transactions={rawTransactions.slice(0, COLLAPSED_ROWS)}
            {...listProps}
          />
          {/* The rest unfold when the card takes over the Inbox. Natural
              height either way: the page is the only thing that scrolls. */}
          <Unfold open={expanded}>
            {rawTransactions.length > COLLAPSED_ROWS && (
              <RawTransactionList
                className="border-border border-t"
                transactions={rawTransactions.slice(COLLAPSED_ROWS)}
                {...listProps}
              />
            )}
            {hasMore && (
              <div className="border-border flex justify-center border-t">
                <MoreButton
                  loading={rawLoading}
                  hasMore
                  onMore={handleMore}
                  label={moreLabel}
                />
              </div>
            )}
          </Unfold>
        </>
      )}
      <RawSelectionBar
        actions={selectionActions}
        open={gestures.selecting}
        onCancel={gestures.stopSelecting}
      />
    </div>
  );
};

const COLLAPSED_ROWS = 3;

/**
 * Height that animates between nothing and its content's natural height (a
 * grid row going 0fr → 1fr), so no measuring. Closed, its contents are inert:
 * out of the tab order, and not something a tap can land on.
 */
const Unfold = ({open, children}: {open: boolean; children: React.ReactNode}) => {
  const innerRef = React.useRef<HTMLDivElement>(null);
  // React 18 has no `inert` prop, hence the ref.
  React.useEffect(() => {
    if (innerRef.current) innerRef.current.inert = !open;
  }, [open]);
  return (
    <div
      className="grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none"
      style={{gridTemplateRows: open ? '1fr' : '0fr'}}
    >
      <div ref={innerRef} className="min-h-0 overflow-hidden">
        {children}
      </div>
    </div>
  );
};
